/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spreadsheet from 'react-spreadsheet';
import Loading from '../../../../components/Loading';
import useStore from '../../../../store';
import { isNullOrBlank } from '../../../../utils/checker';
import { getCompanyTypesByFilterOptionValue, getDefaultFilterOptionValue } from '../../../../utils/ews';
import { calculateSFPCells } from '../../../../utils/ews-sfp-utils';
import { toFormatCurrency, toNumberFromCurrency } from '../../../../utils/format';
import { checkSelectedActiveColumn, mappingKhmerNumber } from '../../../../utils/market-development';
import { getYear } from '../../utils';
import EWSLabels from '../ews-template-labels';

const Q_REPORT = gql`
	query getAnnuallyEarlyWarningSFPSummaryReport(
		$years: [Float!]
		$companyTypes: [InsuranceInstitutionsType!]
		$companyId: String
		$type: EarlyWarningTypeStatement!
	) {
		getAnnuallyEarlyWarningSFPSummaryReport(years: $years, companyTypes: $companyTypes, companyId: $companyId, type: $type)
	}
`;

export default function SFPSpreadSheetTable({
	filterOptions = [],
	type = null,
	years = null,
	companyType = null,
	companyId = null,
	onReadyForExport = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState(null);
	const [tables, setTables] = useState([]);
	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);

	useEffect(load, [years, companyType, companyId, type, filterOptions]);

	useEffect(() => data?.getAnnuallyEarlyWarningSFPSummaryReport && setDetail(data?.getAnnuallyEarlyWarningSFPSummaryReport), [data]);

	useEffect(() => calculateTables(), [detail]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = {
			years: years ? JSON.parse(years).map((year) => parseInt(year)) : [getYear(null)],
			companyTypes: getCompanyTypes(companyType),
			companyId,
			type,
		};
		loadReport({ variables });
	}

	function getCompanyTypes(value) {
		return getCompanyTypesByFilterOptionValue(value ? value : getDefaultFilterOptionValue({ filterOptions }));
	}

	function calculateTables() {
		if (detail?.result) {
			const localTables = Object.keys(detail?.result).reduce((tables, key) => {
				const localTables = tables;
				const tableCells = getCells({ code: 'SFP', labels: detail?.labels, tableCells: detail?.result[key] });
				if (tableCells) {
					localTables.push({ tableCells, key });
				}
				return localTables;
			}, []);
			const sorts = localTables?.sort((a, b) => (parseInt(a?.key) > parseInt(b?.key) ? 1 : -1));
			if (sorts?.length === 2) {
				const oldTableCells = sorts[0]?.tableCells;
				const newTableCells = sorts[1]?.tableCells;
				const key = 'Comparison %';
				const tableCells = newTableCells?.reduce((p, c, i) => {
					let lPre = p;
					const old0 = oldTableCells[i][0]?.value;
					const old1 = oldTableCells[i][1]?.value;
					const new0 = c[0]?.value;
					const new1 = c[1]?.value;
					const isBlanked = isNullOrBlank(old0) || isNullOrBlank(old1) || isNullOrBlank(new0) || isNullOrBlank(new1);
					if (isBlanked) {
						lPre.push([
							{ value: '', readOnly: true },
							{ value: '', readOnly: true },
						]);
					} else {
						const oldNum0 = toNumberFromCurrency(old0);
						const oldNum1 = toNumberFromCurrency(old1);
						const newNum0 = toNumberFromCurrency(new0);
						const newNum1 = toNumberFromCurrency(new1);
						lPre.push([
							{
								value: toFormatCurrency(oldNum0 === 0 ? 0 : (newNum0 / oldNum0 - 1) * 100, null) + '%',
								readOnly: true,
							},
							{
								value: toFormatCurrency(oldNum1 === 0 ? 0 : (newNum1 / oldNum1 - 1) * 100, null) + '%',
								readOnly: true,
							},
						]);
					}
					return lPre;
				}, []);
				setTables([...sorts, { tableCells, key }]);
			} else {
				setTables(sorts);
			}
			onReadyForExport({ labels: detail?.labels, tables: localTables });
		}
	}

	function getCells({ code = null, labels = [], tableCells = null }) {
		if (tableCells) {
			const localCells = labels?.map((label) => {
				const values = [
					{ value: 0, readOnly: true },
					{ value: 0, readOnly: true },
				];
				const labelObject = tableCells?.find((e) => e?.code === label?.code);
				return labelObject?.values || values;
			});
			const { cells } = calculateSFPCells({
				code,
				cells: localCells,
				numLabel: labels?.length,
				readOnly: true,
			});
			const formattedCells = cells.map((row) => {
				return row.map((cell, colIndex) => {
					return {
						...cell,
						value: (() => {
							if (false === isNullOrBlank(cell?.value)) {
								if (colIndex === 0) {
									return toFormatCurrency(cell?.value);
								} else {
									return toFormatCurrency(cell?.value, null);
								}
							}
							return cell?.value;
						})(),
					};
				});
			});
			return formattedCells;
		}
		return null;
	}

	return (
		<>
			<Box>
				{loading && (
					<>
						<Loading />
					</>
				)}
				{!loading && !detail && (
					<>
						<Text color="gray.400" textAlign="center" mt={4}>
							{t('No Data')}
						</Text>
					</>
				)}
				{!loading && detail && (
					<>
						<Box className="ews-container">
							<div className="ews-2-row-head">{t('descriptionReport')}</div>
							<Flex>
								{tables.map((table, i) => (
									<table key={i} className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver" style={{ color: 'var(--chakra-colors-blue-600)' }}>
													{lang === 'kh' ? t(mappingKhmerNumber(table?.key)) : t(table?.key)}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td
													className={checkSelectedActiveColumn(active, i * 2 + 0) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t('USD')}
												</td>
												<td
													className={checkSelectedActiveColumn(active, i * 2 + 1) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								))}
							</Flex>
							<div>
								<EWSLabels type="EWS" labels={detail?.labels} active={active} blueDesc={true} />
								<Flex>
									{tables?.map((table, i) => (
										<Spreadsheet
											key={i}
											data={table?.tableCells}
											onChange={(cells) => {
												const tableCells = table?.tableCells?.reduce((pre, cur, i) => {
													let localPre = pre;
													localPre.push([
														{ value: cur[0]?.value, readOnly: true },
														{ value: cur[1]?.value, readOnly: true },
													]);
													return localPre;
												}, []);
												const localTable = { ...table, tableCells };
												const localTables = tables;
												localTables[i] = localTable;
												setTables(localTables);
											}}
											onActivate={(e) => setActive({ ...e, column: i * 2 + e?.column, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									))}
								</Flex>
							</div>
						</Box>
					</>
				)}
			</Box>
		</>
	);
}
